import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../TopNavbar';
import Footer from '../../Footer/Footer';

const LeadershipDetail = () => {
    const { role } = useParams();

    const [leaderInfo, setLeaderInfo] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activitiesLoading, setActivitiesLoading] = useState(true);

    const cleanRoleKey = role ? role.toLowerCase() : '';
    const formattedRoleName = role ? role.replace(/-/g, ' ').toUpperCase() : 'LEADER';

    // 1. Fetch collaborator/leader info
    useEffect(() => {
        const fetchLeaderData = async () => {
            setLoading(true);
            try {
                const response = await fetch('https://api.lionsmd320.org/pages/api/collaborators');
                const data = await response.json();

                if (response.ok && data.collaborators) {
                    const foundLeader = data.collaborators.find(
                        item => item.role.toLowerCase() === cleanRoleKey
                    );

                    if (foundLeader) {
                        setLeaderInfo({
                            name: foundLeader.name,
                            description: foundLeader.introduction || 'Here you can find updates, strategic objectives, and resources coordinated by our dedicated district team members.'
                        });
                    } else {
                        setLeaderInfo({
                            name: formattedRoleName,
                            description: 'Here you can find updates, strategic objectives, and resources coordinated by our dedicated district team members.'
                        });
                    }
                }
            } catch (err) {
                console.error('Error fetching leadership details:', err);
                setLeaderInfo({
                    name: formattedRoleName,
                    description: 'Here you can find updates, strategic objectives, and resources coordinated by our dedicated district team members.'
                });
            } finally {
                setLoading(false);
            }
        };

        fetchLeaderData();
    }, [cleanRoleKey, formattedRoleName]);

    // 2. Fetch Activities & Gallery data from API
    useEffect(() => {
        const fetchActivitiesData = async () => {
            setActivitiesLoading(true);
            try {
                const response = await fetch('https://api.lionsmd320.org/pages/api/api/activities');
                const data = await response.json();

                if (response.ok && data.multipleDistricts) {
                    // Filter activities where district matches and isActive is 1
                    const matchedActivities = data.multipleDistricts.filter(
                        item => item.district &&
                            item.district.toLowerCase() === cleanRoleKey &&
                            Number(item.isActive) === 1
                    );
                    setActivities(matchedActivities);
                }
            } catch (err) {
                console.error('Error fetching activities:', err);
            } finally {
                setActivitiesLoading(false);
            }
        };

        if (cleanRoleKey) {
            fetchActivitiesData();
        }
    }, [cleanRoleKey]);

    return (
        <>
            <Header />
            <div style={{ backgroundColor: '#f9fafb', minHeight: '100vh', paddingBottom: '64px', paddingTop: '32px' }}>

                {/* Main Content Container */}
                <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 16px' }}>

                    {/* Profile Header Banner */}
                    <div style={{ backgroundColor: '#0067B1', color: '#ffffff', borderRadius: '16px', padding: '36px', marginBottom: '32px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                        <span style={{ color: '#FFD500', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                            Leadership Profile
                        </span>
                        <h1 style={{ fontSize: '32px', fontWeight: 800, marginTop: '8px', marginBottom: '8px' }}>
                            {loading ? 'Loading...' : (leaderInfo?.name || formattedRoleName)}
                        </h1>
                        <p style={{ color: '#e0f2fe', fontSize: '16px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                            {formattedRoleName}
                        </p>
                    </div>

                    {/* Two-Column Grid Layout */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px', alignItems: 'start', marginBottom: '32px' }}>

                        {/* LEFT COLUMN: Introduction */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                            <section style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6' }}>
                                <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#0067B1', marginBottom: '16px', borderBottom: '2px solid #FFD500', display: 'inline-block', paddingBottom: '4px' }}>
                                    Introduction
                                </h2>
                                <p style={{ color: '#4b5563', fontSize: '15px', lineHeight: '1.6' }}>
                                    {loading ? 'Loading introduction...' : (
                                        <>
                                            Welcome to the official portfolio portal for <strong>{leaderInfo?.name}</strong> ({formattedRoleName}). {leaderInfo?.description}
                                        </>
                                    )}
                                </p>
                            </section>
                        </div>

                        {/* RIGHT COLUMN: Submit a Document Widget Card */}
                        <div>
                            <div style={{ position: 'sticky', top: '24px', backgroundColor: '#FFD500', borderRadius: '16px', padding: '28px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                                <h3 style={{ fontWeight: 800, color: '#0067B1', fontSize: '20px', marginBottom: '10px' }}>
                                    Submit a Document
                                </h3>
                                <p style={{ color: 'rgba(0, 103, 177, 0.8)', fontSize: '14px', lineHeight: '1.5', marginBottom: '20px' }}>
                                    Upload official reports, activity logs, or administrative resources directly to <strong>{leaderInfo?.name || formattedRoleName}</strong>'s office.
                                </p>
                                <Link to="/upload" style={{ display: 'block', backgroundColor: '#0067B1', color: '#ffffff', fontWeight: 'bold', fontSize: '14px', padding: '12px 16px', borderRadius: '8px', textAlign: 'center', textDecoration: 'none', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                    Upload Document Now
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Activities & Gallery Section (Mapped dynamically with isActive check) */}
                    <section style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6' }}>
                        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#0067B1', marginBottom: '20px' }}>
                            Activities & Gallery ({formattedRoleName})
                        </h2>

                        {activitiesLoading ? (
                            <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading activities and gallery...</p>
                        ) : activities.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
                                {activities.map((item) => (
                                    <div key={item.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                                        {item.imageUrl ? (
                                            <div style={{ height: '180px', width: '100%', overflow: 'hidden', backgroundColor: '#f3f4f6' }}>
                                                <img src={item.imageUrl} alt={item.caption} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            </div>
                                        ) : (
                                            <div style={{ height: '180px', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                                                No Image Available
                                            </div>
                                        )}
                                        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}>
                                            <span style={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: '4px', alignSelf: 'flex-start' }}>
                                                {item.activity}
                                            </span>
                                            <p style={{ fontSize: '14px', color: '#374151', margin: 0, fontWeight: 500 }}>
                                                {item.caption}
                                            </p>
                                            <span style={{ fontSize: '12px', color: '#9ca3af', marginTop: 'auto' }}>
                                                {new Date(item.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ padding: '32px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                                    No active activities or gallery images found for district <strong>{formattedRoleName}</strong>.
                                </p>
                            </div>
                        )}
                    </section>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default LeadershipDetail;